import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { Modal, VideoPlayer, Thumb, ImagePair } from '../components/ui';

const CATEGORIES = ['Pectoraux', 'Dos', 'Épaules', 'Biceps', 'Triceps', 'Jambes', 'Fessiers', 'Abdominaux', 'Mollets', 'Avant-bras', 'Trapèzes', 'Cardio', 'Autre'];
const MEDIA_FIELDS = [
  { kind: 'image', column: 'image_url', label: 'Image (départ)', accept: 'image/*' },
  { kind: 'image_end', column: 'image_url_end', label: 'Image (fin de mouvement)', accept: 'image/*' },
  { kind: 'video', column: 'video_url', label: 'Vidéo (lien YouTube, URL ou fichier)', accept: 'video/*' },
];

const EMPTY = {
  name: '', category: 'Pectoraux', equipment: '', secondary_muscles: '', description: '', tips: '',
  default_rest_seconds: 90, is_timed: false, image_url: '', image_url_end: '', video_url: '',
};

export function LibraryForm({ initial, onSaved, onCancel }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ ...EMPTY, ...Object.fromEntries(Object.entries(initial || {}).map(([k, v]) => [k, v ?? ''])) });
  const [files, setFiles] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const payload = {
        ...form,
        default_rest_seconds: parseInt(form.default_rest_seconds, 10) || 0,
        is_timed: !!form.is_timed,
      };
      MEDIA_FIELDS.forEach((m) => { if (files[m.kind]) payload[m.column] = ''; });
      let saved = initial?.id ? await api.updateLibraryItem(initial.id, payload) : await api.createLibraryItem(payload);
      for (const m of MEDIA_FIELDS) {
        if (files[m.kind]) saved = await api.uploadLibraryMedia(saved.id, m.kind, files[m.kind]);
      }
      qc.invalidateQueries({ queryKey: ['library'] });
      qc.invalidateQueries({ queryKey: ['programs'] });
      qc.invalidateQueries({ queryKey: ['exercises'] });
      onSaved(saved);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="form-grid">
      <div className="form-group span-2">
        <label className="form-label">Nom *</label>
        <input className="form-input" value={form.name} onChange={(e) => set('name', e.target.value)} required maxLength={120} />
      </div>
      <div className="form-group">
        <label className="form-label">Catégorie</label>
        <select className="form-select" value={form.category} onChange={(e) => set('category', e.target.value)}>
          {[...new Set([...CATEGORIES, form.category])].map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">Équipement</label>
        <input className="form-input" value={form.equipment} onChange={(e) => set('equipment', e.target.value)} placeholder="Barre, haltères…" />
      </div>
      <div className="form-group span-2">
        <label className="form-label">Muscles secondaires</label>
        <input className="form-input" value={form.secondary_muscles} onChange={(e) => set('secondary_muscles', e.target.value)} />
      </div>
      <div className="form-group span-2">
        <label className="form-label">Description</label>
        <textarea className="form-input" rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} />
      </div>
      <div className="form-group span-2">
        <label className="form-label">Conseils d'exécution</label>
        <textarea className="form-input" rows={2} value={form.tips} onChange={(e) => set('tips', e.target.value)} />
      </div>
      <div className="form-group">
        <label className="form-label">Repos par défaut (s)</label>
        <input type="number" min="0" max="900" className="form-input" value={form.default_rest_seconds} onChange={(e) => set('default_rest_seconds', e.target.value)} />
      </div>
      <label className="check-row">
        <input type="checkbox" checked={!!form.is_timed} onChange={(e) => set('is_timed', e.target.checked)} />
        Exercice chronométré (secondes)
      </label>

      {MEDIA_FIELDS.map((m) => (
        <div className="form-group span-2" key={m.kind}>
          <label className="form-label">{m.label}</label>
          <input
            className="form-input"
            placeholder="https://… ou /api/media/…"
            value={files[m.kind] ? '' : form[m.column]}
            disabled={!!files[m.kind]}
            onChange={(e) => set(m.column, e.target.value)}
          />
          <div className="media-actions">
            <input
              type="file"
              accept={m.accept}
              onChange={(e) => setFiles((f) => ({ ...f, [m.kind]: e.target.files[0] || null }))}
            />
            {form[m.column] && !files[m.kind] && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => set(m.column, '')}>Retirer</button>
            )}
          </div>
        </div>
      ))}

      {error && <div className="alert-error span-2">{error}</div>}
      <div className="form-actions span-2">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Annuler</button>
        <button className="btn btn-primary" disabled={busy}>{busy ? 'Enregistrement…' : 'Enregistrer'}</button>
      </div>
    </form>
  );
}

function Detail({ id, onClose, onEdit }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['library', id], queryFn: () => api.getLibraryItem(id) });
  const del = useMutation({
    mutationFn: () => api.deleteLibraryItem(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['library'] }); onClose(); },
    onError: (e) => alert(e.message),
  });
  if (!data) return <Modal title="Chargement…" onClose={onClose}><div className="spinner" /></Modal>;
  return (
    <Modal title={data.name} onClose={onClose} wide>
      <div className="chips">
        <span className="chip">{data.category}</span>
        {data.equipment && <span className="chip">{data.equipment}</span>}
        {data.is_timed ? <span className="chip">⏱ Chronométré</span> : null}
      </div>
      <ImagePair start={data.image_url} end={data.image_url_end} alt={data.name} />
      <VideoPlayer url={data.video_url} />
      {data.secondary_muscles && <p className="muted"><strong>Muscles secondaires :</strong> {data.secondary_muscles}</p>}
      {data.description && <p>{data.description}</p>}
      {data.tips && <p className="tip-box">💡 {data.tips}</p>}
      {data.best?.max_weight != null && (
        <p><strong>🏆 Record perso :</strong> {data.best.max_weight} kg</p>
      )}
      {data.programs.length > 0 && <p className="muted">Utilisé dans : {data.programs.join(', ')}</p>}
      <div className="form-actions">
        <button className="btn btn-danger btn-sm" onClick={() => window.confirm('Supprimer cet exercice de la bibliothèque ?') && del.mutate()}>Supprimer</button>
        <button className="btn btn-primary btn-sm" onClick={() => onEdit(data)}>✏️ Modifier</button>
      </div>
    </Modal>
  );
}

export default function Exercises() {
  const { data: library = [], isLoading } = useQuery({ queryKey: ['library'], queryFn: api.getLibrary });
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [detailId, setDetailId] = useState(null);
  const [form, setForm] = useState(null);

  const categories = useMemo(() => [...new Set(library.map((l) => l.category))].sort(), [library]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return library.filter((l) => (!category || l.category === category)
      && (!q || `${l.name} ${l.equipment || ''} ${l.secondary_muscles || ''}`.toLowerCase().includes(q)));
  }, [library, search, category]);

  if (isLoading) return <div className="loading"><div className="spinner" />Chargement…</div>;

  return (
    <div>
      <div className="page-head">
        <div>
          <h1 className="page-title">Bibliothèque d'exercices</h1>
          <p className="page-subtitle">{library.length} exercices avec images et vidéos. Crée et modifie les tiens.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setForm({})}>＋ Nouvel exercice</button>
      </div>

      <div className="toolbar">
        <input className="form-input" placeholder="🔎 Rechercher…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="chips scroll-x">
          <button className={'chip chip-btn' + (!category ? ' active' : '')} onClick={() => setCategory('')}>Tous</button>
          {categories.map((c) => (
            <button key={c} className={'chip chip-btn' + (category === c ? ' active' : '')} onClick={() => setCategory(c)}>{c}</button>
          ))}
        </div>
      </div>

      <div className="lib-grid">
        {filtered.map((l) => (
          <button key={l.id} className="lib-card" onClick={() => setDetailId(l.id)}>
            <div className="lib-card-img">
              {l.image_url ? <img src={l.image_url} alt={l.name} loading="lazy" /> : <Thumb size={64} />}
              {l.video_url && <span className="lib-play">▶</span>}
            </div>
            <div className="lib-card-body">
              <div className="lib-card-name">{l.name}</div>
              <div className="muted small">{l.category}{l.equipment ? ` · ${l.equipment}` : ''}</div>
            </div>
          </button>
        ))}
        {filtered.length === 0 && <div className="empty-state">Aucun exercice trouvé.</div>}
      </div>

      {detailId && !form && (
        <Detail id={detailId} onClose={() => setDetailId(null)} onEdit={(item) => setForm(item)} />
      )}
      {form && (
        <Modal title={form.id ? 'Modifier l\'exercice' : 'Nouvel exercice'} onClose={() => setForm(null)} wide>
          <LibraryForm
            initial={form.id ? form : null}
            onCancel={() => setForm(null)}
            onSaved={(saved) => { setForm(null); setDetailId(saved.id); }}
          />
        </Modal>
      )}
    </div>
  );
}
